// Compares patient data extracted from an imported exam file against the
// patient record it was matched to. A field is only ever reported as a
// mismatch when both sides are present and genuinely disagree — a value the
// file did not supply is unknown, not contradictory.
//
// Comparison and display are deliberately separate: a record birthdate is a
// full timestamp and a file sex is a single DICOM letter, so the values are
// normalized before display too, otherwise agreeing fields would still look
// different on screen.

export type MatchFieldType = {
  label: string;
  fileValue: string | null;
  recordValue: string | null;
  isMismatch: boolean;
};

const collapseWhitespace = (value?: string | null): string =>
  (value ?? "").trim().replace(/\s+/g, " ");

const hasValue = (value?: string | null): boolean =>
  collapseWhitespace(value).length > 0;

export const getFullName = (
  firstName?: string | null,
  lastName?: string | null
): string => collapseWhitespace(`${firstName ?? ""} ${lastName ?? ""}`);

// Dates arrive as full timestamps from the patient record and as date-only
// strings from the file, so only the date portion is comparable
export const getBirthdateOnly = (value?: string | null): string =>
  collapseWhitespace(value).slice(0, 10);

// Patient records store "Male"/"Female" while imported files use the DICOM
// single-letter form. An unrecognized value is displayed as it was received but
// treated as unknown for comparison, so it is never reported as a mismatch.
export const getSexLabel = (value?: string | null): string => {
  switch (collapseWhitespace(value).toLowerCase()) {
    case "m":
    case "male":
      return "Male";
    case "f":
    case "female":
      return "Female";
    case "o":
    case "other":
      return "Other";
    default:
      return collapseWhitespace(value);
  }
};

const isRecognizedSex = (value?: string | null): boolean =>
  ["Male", "Female", "Other"].includes(getSexLabel(value));

// Health care numbers are written inconsistently across sources
// Ex: 1234-5678 and 1234 5678 are the same number as 12345678
const compactHealthCareId = (value?: string | null): string =>
  (value ?? "").replace(/[\s-]/g, "").toLowerCase();

// A missing value on either side is unknown, so it can never disagree
const compare = (fileValue: string, recordValue: string): boolean =>
  hasValue(fileValue) && hasValue(recordValue) && fileValue !== recordValue;

const toDisplayValue = (value: string): string | null =>
  hasValue(value) ? value : null;

const buildField = (
  label: string,
  fileValue: string,
  recordValue: string,
  isMismatch: boolean
): MatchFieldType => ({
  label,
  fileValue: toDisplayValue(fileValue),
  recordValue: toDisplayValue(recordValue),
  isMismatch,
});

export const buildNameField = (
  label: string,
  fileValue?: string | null,
  recordValue?: string | null
): MatchFieldType => {
  const file = collapseWhitespace(fileValue);
  const record = collapseWhitespace(recordValue);

  return buildField(
    label,
    file,
    record,
    compare(file.toLowerCase(), record.toLowerCase())
  );
};

export const buildBirthdateField = (
  label: string,
  fileValue?: string | null,
  recordValue?: string | null
): MatchFieldType => {
  const file = getBirthdateOnly(fileValue);
  const record = getBirthdateOnly(recordValue);

  return buildField(label, file, record, compare(file, record));
};

export const buildHealthCareIdField = (
  label: string,
  fileValue?: string | null,
  recordValue?: string | null
): MatchFieldType => {
  const file = collapseWhitespace(fileValue);
  const record = collapseWhitespace(recordValue);

  return buildField(
    label,
    file,
    record,
    compare(compactHealthCareId(file), compactHealthCareId(record))
  );
};

export const buildSexField = (
  label: string,
  fileValue?: string | null,
  recordValue?: string | null
): MatchFieldType => {
  const file = getSexLabel(fileValue);
  const record = getSexLabel(recordValue);

  // an unrecognized value is shown but not compared
  const isComparable =
    isRecognizedSex(fileValue) && isRecognizedSex(recordValue);

  return buildField(label, file, record, isComparable && compare(file, record));
};
